import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { DropletOff, Droplets, Plus, X } from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import {
  collectFactoryAttentionKeys,
  markFactoryAttentionSeen,
} from '../factory/factoryAttentionSeen';
import {
  factoryOrderAttentionHint,
  factoryOrderDisplayTitle,
  factoryOrderFactoryStepLabel,
  factoryOrderFilterBucket,
  factoryOrderReleaseUi,
  factoryOrderStatusLabel,
  factoryService,
  isFactoryBatchOwnerAccepted,
  type FactoryBatch,
  type FactoryOrder,
  type FactoryOrderBatchProgress,
  type FactoryOrderListFilter,
  sortFactoryOrdersByTime,
} from '../services/factoryService';
import {
  factoryStaffService,
  type FactoryWorkerDirectoryEntry,
} from '../services/factoryStaffService';

const OWNER_MAX_SLOTS = 3;
const FILTERS: { key: FactoryOrderListFilter; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'active', label: '进行中' },
  { key: 'done', label: '已完成' },
  { key: 'cancelled', label: '已取消' },
];

type LocationState = {
  filter?: FactoryOrderListFilter;
  resumeAccept?: {
    orderId: string;
    batchId: string;
    pendingStart?: boolean;
  };
};

type AcceptDialogState = {
  orderId: string;
  batchId: string;
  pendingStart: boolean;
};

/** Learning-shell Owner entry: up to 3 concurrent order slots (no FactoryWorker required). */
export function LearningFactoryOrdersPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const locationState = (location.state as LocationState | null) || {};
  const resumeFilter = locationState.filter;
  const [orders, setOrders] = useState<FactoryOrder[]>([]);
  const [history, setHistory] = useState<FactoryOrder[]>([]);
  const [pendingBatches, setPendingBatches] = useState<FactoryBatch[]>([]);
  const [workers, setWorkers] = useState<FactoryWorkerDirectoryEntry[]>([]);
  const [filter, setFilter] = useState<FactoryOrderListFilter>(resumeFilter || 'done');
  const [note, setNote] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [workerKey, setWorkerKey] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [workersLoading, setWorkersLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [acceptDialog, setAcceptDialog] = useState<AcceptDialogState | null>(null);

  useEffect(() => {
    if (resumeFilter) setFilter(resumeFilter);
  }, [resumeFilter]);

  useEffect(() => {
    const resume = locationState.resumeAccept;
    if (!resume?.orderId || !resume?.batchId) return;
    setAcceptDialog({
      orderId: resume.orderId,
      batchId: resume.batchId,
      pendingStart: Boolean(resume.pendingStart),
    });
    navigate(location.pathname, {
      replace: true,
      state: { filter: resumeFilter || filter } satisfies LocationState,
    });
  }, [filter, location.pathname, locationState.resumeAccept, navigate, resumeFilter]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 2000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  function showToast(text: string) {
    setToast(text);
  }

  const reload = useCallback(async () => {
    const [next, pending, pendingRelease] = await Promise.all([
      factoryService.listMyOrdersWithHistory(),
      factoryService.listOwnerPendingBatches().catch(() => [] as FactoryBatch[]),
      factoryService.listOwnerPendingReleaseBatches().catch(() => [] as FactoryBatch[]),
    ]);
    setOrders(next.orders);
    setHistory(next.history);
    setPendingBatches(pending);
    if (user?.id) {
      markFactoryAttentionSeen(
        user.id,
        collectFactoryAttentionKeys({
          orders: next.orders || [],
          pendingAuth: pending,
          pendingRelease,
        }),
      );
    }
  }, [user?.id]);

  const allOrders = useMemo(
    () => [...orders, ...history].sort(sortFactoryOrdersByTime),
    [history, orders],
  );

  const filterCounts = useMemo(() => {
    const counts: Record<FactoryOrderListFilter, number> = {
      all: allOrders.length,
      active: 0,
      done: 0,
      cancelled: 0,
    };
    for (const order of allOrders) {
      counts[factoryOrderFilterBucket(order)] += 1;
    }
    return counts;
  }, [allOrders]);

  const activeSlotCount = filterCounts.active;
  const canCreate = activeSlotCount < OWNER_MAX_SLOTS;

  const filteredOrders = useMemo(() => {
    if (filter === 'all') return allOrders;
    return allOrders.filter((o) => factoryOrderFilterBucket(o) === filter);
  }, [allOrders, filter]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await reload();
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  useEffect(() => {
    if (!orders.length) return;
    const timer = window.setInterval(() => {
      void reload().catch(() => undefined);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [orders.length, reload]);

  async function openCreate() {
    if (!canCreate) {
      setError(`每人最多同时保留 ${OWNER_MAX_SLOTS} 个进行中的订单`);
      return;
    }
    setError(null);
    setDialogError(null);
    setNote('');
    setAdvancedOpen(false);
    setWorkerKey('');
    setCreateOpen(true);
    setWorkersLoading(true);
    try {
      const list = await factoryStaffService.listApprovedWorkers();
      setWorkers(list);
      if (list.length === 1) {
        setWorkerKey(`${list[0].userId}:${list[0].accountId}`);
      }
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : String(err));
    } finally {
      setWorkersLoading(false);
    }
  }

  function closeCreate() {
    if (busy) return;
    setCreateOpen(false);
    setDialogError(null);
  }

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    const selected = workers.find((w) => `${w.userId}:${w.accountId}` === workerKey);
    if (!selected) {
      setDialogError('请选择工厂工作人员账户');
      return;
    }
    setBusy(true);
    setDialogError(null);
    try {
      await factoryService.createOrder({
        note: note.trim() || undefined,
        preferredWorkerUserId: selected.userId,
        preferredWorkerAccountId: selected.accountId,
      });
      setNote('');
      setWorkerKey('');
      setCreateOpen(false);
      showToast('订单已发布到颗粒工厂，等待指定工人接单。');
      await reload();
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onCancel(orderId: string) {
    setBusyId(orderId);
    setError(null);
    try {
      await factoryService.cancelOrder(orderId);
      showToast('订单已取消，槽位已释放。');
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  async function onAuthorize(
    batchId: string,
    decision: 'authorize_factory' | 'authorize_self' | 'reject',
  ) {
    setBusyId(batchId);
    setError(null);
    try {
      await factoryService.authorizeOwnerBatch(batchId, decision);
      showToast(
        decision === 'authorize_factory'
          ? '已授权工厂加工，版面工序将启动。'
          : decision === 'authorize_self'
            ? '已选择自行处理。'
            : '已拒绝该批次加工。',
      );
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  async function onAuthorizeRelease(
    order: FactoryOrder,
    batchId: string,
    decision: 'start' | 'stop',
  ) {
    if (decision === 'start') {
      const batch = (order.batches || []).find((b) => b.id === batchId);
      if (batch && !isFactoryBatchOwnerAccepted(batch)) {
        setAcceptDialog({ orderId: order.id, batchId, pendingStart: true });
        return;
      }
    }
    setBusyId(batchId);
    setError(null);
    try {
      await factoryService.authorizeOwnerRelease(batchId, decision);
      showToast(
        decision === 'start'
          ? '已开始释放成品智能体；学习侧将按背压滴灌品题颗粒。'
          : '已停止释放成品智能体。',
      );
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  async function onAcceptGoods(options?: { startAfter?: boolean }) {
    if (!acceptDialog) return;
    const { batchId, pendingStart } = acceptDialog;
    const shouldStart = Boolean(options?.startAfter ?? pendingStart);
    setBusyId(batchId);
    setError(null);
    try {
      await factoryService.acceptOwnerGoods(batchId);
      if (shouldStart) {
        await factoryService.authorizeOwnerRelease(batchId, 'start');
        showToast('已验收并开始释放成品智能体。');
      } else {
        showToast('已验收；验收后不可撤回。');
      }
      setAcceptDialog(null);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  function openAcceptOnly(orderId: string, batchId: string) {
    setAcceptDialog({ orderId, batchId, pendingStart: false });
  }

  function viewParticlesFromAccept() {
    if (!acceptDialog) return;
    navigate(`/factory-orders/${acceptDialog.orderId}`, {
      state: {
        filter,
        resumeAccept: {
          orderId: acceptDialog.orderId,
          batchId: acceptDialog.batchId,
          pendingStart: acceptDialog.pendingStart,
        },
      } satisfies LocationState,
    });
  }

  const orphanPending = pendingBatches.filter((b) => {
    if (!b.orderId) return true;
    return !orders.some((o) => o.id === b.orderId);
  });

  const showOrphans = (filter === 'all' || filter === 'active') && orphanPending.length > 0;

  function renderAuthActions(batchId: string) {
    return (
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busyId === batchId}
          onClick={() => void onAuthorize(batchId, 'authorize_factory')}
          className="rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
        >
          {busyId === batchId ? '处理中…' : '授权工厂'}
        </button>
        <button
          type="button"
          disabled={busyId === batchId}
          onClick={() => void onAuthorize(batchId, 'authorize_self')}
          className="rounded-lg border border-border bg-surface px-3 py-1.5 text-xs text-ink disabled:opacity-50"
        >
          自己处理
        </button>
        <button
          type="button"
          disabled={busyId === batchId}
          onClick={() => void onAuthorize(batchId, 'reject')}
          className="rounded-lg border border-danger/40 px-3 py-1.5 text-xs text-danger disabled:opacity-50"
        >
          拒绝
        </button>
      </div>
    );
  }

  function renderAuthBlock(batch: FactoryOrderBatchProgress) {
    if (batch.processingAuthorization !== 'awaiting_owner_auth') return null;
    return (
      <div key={batch.id} className="space-y-2 rounded-lg border border-border/70 bg-canvas/40 px-3 py-2">
        <div className="truncate text-xs font-medium text-ink">{batch.title || '未命名批次'}</div>
        {renderAuthActions(batch.id)}
      </div>
    );
  }

  function renderReleaseRow(order: FactoryOrder) {
    const release = factoryOrderReleaseUi(order);
    if (!release) return null;
    const { batch, mode } = release;
    const awaiting = mode === 'awaiting';
    const accepted = isFactoryBatchOwnerAccepted(batch);
    return (
      <div className="border-t border-border/70 px-3 py-2.5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span
            className={`inline-flex min-w-0 items-center gap-2 text-xs font-semibold ${
              awaiting ? 'text-danger' : 'text-success'
            }`}
          >
            {awaiting ? (
              <DropletOff size={16} className="shrink-0 text-danger" aria-hidden />
            ) : (
              <Droplets
                size={16}
                className="factory-droplets-drip shrink-0 text-success"
                aria-hidden
              />
            )}
            {awaiting ? '颗粒待释放' : '颗粒释放中'}
          </span>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            {!accepted ? (
              <button
                type="button"
                disabled={busyId === batch.id}
                onClick={() => openAcceptOnly(order.id, batch.id)}
                className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-900 disabled:opacity-50"
              >
                确认验收
              </button>
            ) : null}
            <button
              type="button"
              disabled={busyId === batch.id}
              onClick={() => void onAuthorizeRelease(order, batch.id, awaiting ? 'start' : 'stop')}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium disabled:opacity-50 ${
                awaiting
                  ? 'bg-primary text-white'
                  : 'border border-border bg-surface text-ink'
              }`}
            >
              {busyId === batch.id
                ? '处理中…'
                : awaiting
                  ? '开始释放'
                  : '停止释放'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const dialog = createOpen ? (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/45 p-4"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) closeCreate();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="factory-order-create-title"
        className="w-full max-w-md rounded-2xl bg-surface p-5 shadow-card"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 id="factory-order-create-title" className="text-lg font-semibold text-ink">
              新建工厂订单
            </h3>
            <p className="mt-1 text-sm text-muted">
              指定工作人员后发布。每人最多同时 3 个进行中的订单。
            </p>
          </div>
          <button
            type="button"
            onClick={closeCreate}
            disabled={busy}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted hover:bg-black/[0.04] hover:text-ink disabled:opacity-50"
            aria-label="关闭"
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={onCreate} className="mt-4 space-y-3">
          <label className="block text-sm">
            <span className="text-muted">工作人员账户</span>
            <select
              className="mt-1 w-full rounded-lg border border-border bg-surface px-3 py-2"
              value={workerKey}
              onChange={(e) => setWorkerKey(e.target.value)}
              disabled={busy || workersLoading}
              required
            >
              <option value="">
                {workersLoading ? '加载中…' : '请选择已批准的厂员'}
              </option>
              {workers.map((w) => (
                <option key={`${w.userId}:${w.accountId}`} value={`${w.userId}:${w.accountId}`}>
                  {w.displayName}
                  {w.role === 'manager' ? ' · 管理员' : ''}
                </option>
              ))}
            </select>
          </label>

          <div className="rounded-lg border border-border">
            <button
              type="button"
              onClick={() => setAdvancedOpen((open) => !open)}
              className="flex w-full items-center justify-between px-3 py-2 text-sm text-muted transition hover:bg-black/[0.03] hover:text-ink"
            >
              <span>高级</span>
              <span className="text-xs">{advancedOpen ? '收起' : '展开'}</span>
            </button>
            {advancedOpen ? (
              <div className="border-t border-border px-3 py-3">
                <label className="block text-sm">
                  <span className="text-muted">备注（可选）</span>
                  <textarea
                    className="mt-1 w-full rounded-lg border border-border px-3 py-2"
                    rows={3}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="可先空着，以实际上传素材为准"
                    disabled={busy}
                  />
                </label>
              </div>
            ) : null}
          </div>

          {dialogError ? <p className="text-sm text-danger">{dialogError}</p> : null}
          {!workersLoading && workers.length === 0 && !dialogError ? (
            <p className="text-sm text-muted">暂无已批准的厂员，请先完成厂员审批。</p>
          ) : null}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              disabled={busy}
              onClick={closeCreate}
              className="rounded-lg border border-border px-4 py-2 text-sm disabled:opacity-60"
            >
              关闭
            </button>
            <button
              type="submit"
              disabled={busy || workersLoading || !workerKey}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
            >
              {busy ? '发布中…' : '发布到工厂'}
            </button>
          </div>
        </form>
      </div>
    </div>
  ) : null;

  const acceptDialogNode = acceptDialog ? (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/45 p-4"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget && busyId !== acceptDialog.batchId) {
          setAcceptDialog(null);
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="factory-accept-title"
        className="w-full max-w-md rounded-2xl bg-surface p-5 shadow-card"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 id="factory-accept-title" className="text-lg font-semibold text-ink">
              成品智能体验收
            </h3>
            <p className="mt-1 text-sm text-muted">
              请确认本组构造的知识颗粒已验收。验收后不可撤回；验收后方可开始释放知识颗粒到复习。
            </p>
          </div>
          <button
            type="button"
            onClick={() => setAcceptDialog(null)}
            disabled={busyId === acceptDialog.batchId}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted hover:bg-black/[0.04] hover:text-ink disabled:opacity-50"
            aria-label="关闭"
          >
            <X size={16} />
          </button>
        </div>

        <div className="mt-4 space-y-2">
          <button
            type="button"
            disabled={busyId === acceptDialog.batchId}
            onClick={viewParticlesFromAccept}
            className="w-full rounded-lg border border-border bg-canvas px-3 py-2.5 text-left text-sm font-medium text-ink hover:bg-black/[0.03] disabled:opacity-50"
          >
            查看本组知识颗粒列表
          </button>
          <p className="text-xs text-muted">
            查看后返回本页，可再次确认验收
            {acceptDialog.pendingStart ? '并开始释放' : ''}。
          </p>
        </div>

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={() => setAcceptDialog(null)}
            disabled={busyId === acceptDialog.batchId}
            className="rounded-lg border border-border px-4 py-2 text-sm disabled:opacity-60"
          >
            取消
          </button>
          <button
            type="button"
            disabled={busyId === acceptDialog.batchId}
            onClick={() => void onAcceptGoods()}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            {busyId === acceptDialog.batchId
              ? '处理中…'
              : acceptDialog.pendingStart
                ? '确认验收并开始释放'
                : '确认验收'}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div className="mx-auto max-w-3xl space-y-5 px-4 py-6">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">颗粒工厂订单</h1>
          <p className="mt-1 text-sm text-muted">
            {loading ? '加载中…' : `进行中 ${activeSlotCount} / ${OWNER_MAX_SLOTS}`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void openCreate()}
          disabled={!canCreate || loading}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="新建订单"
          title={canCreate ? '新建订单' : '已达上限（最多 3 个进行中）'}
        >
          <Plus size={16} />
          新建
        </button>
      </header>

      {error ? (
        <p className="rounded-xl border border-[#F2C6CC] bg-[#FFF3F3] px-4 py-3 text-sm text-danger">{error}</p>
      ) : null}
      {toast ? (
        <div className="pointer-events-none fixed left-1/2 top-4 z-[120] -translate-x-1/2 rounded-lg bg-ink px-4 py-2 text-sm text-white shadow-lg">
          {toast}
        </div>
      ) : null}

      <section className="rounded-2xl border border-border bg-surface p-4 shadow-sm">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((item) => {
            const active = filter === item.key;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => setFilter(item.key)}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                  active
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted hover:bg-black/[0.03]'
                }`}
              >
                {item.label} {filterCounts[item.key]}
              </button>
            );
          })}
        </div>

        {loading ? <p className="mt-3 text-sm text-muted">加载中…</p> : null}
        {!loading && filteredOrders.length === 0 ? (
          <p className="mt-3 text-sm text-muted">当前筛选下没有订单。</p>
        ) : null}

        <ul className="mt-3 space-y-2">
          {filteredOrders.map((order) => {
            const bucket = factoryOrderFilterBucket(order);
            const hint = factoryOrderAttentionHint(order);
            const statusText = factoryOrderStatusLabel[order.status] || order.status;
            const batches = order.batches || [];
            const releaseUi = factoryOrderReleaseUi(order);
            const factoryStep = bucket === 'active' ? factoryOrderFactoryStepLabel(order) : null;
            const when = new Date(order.updatedAt || order.createdAt).toLocaleString();
            const metaPrimary = factoryStep || statusText;
            const accepted = releaseUi
              ? isFactoryBatchOwnerAccepted(releaseUi.batch)
              : (order.batches || []).some((b) => isFactoryBatchOwnerAccepted(b));

            return (
              <li
                key={order.id}
                className={`rounded-xl border ${
                  hint ? 'border-amber-200 bg-amber-50/40' : 'border-border bg-canvas/30'
                }`}
              >
                <button
                  type="button"
                  onClick={() => navigate(`/factory-orders/${order.id}`, { state: { filter } })}
                  className="relative flex w-full flex-col gap-1 px-3 py-3 text-left transition hover:bg-black/[0.02]"
                >
                  {accepted ? (
                    <span className="factory-accept-stamp" aria-label="已验收">
                      已验收
                    </span>
                  ) : null}
                  <div className="flex items-start justify-between gap-3 pr-16">
                    <div className="min-w-0 flex-1 truncate font-medium text-ink">
                      {factoryOrderDisplayTitle(order)}
                    </div>
                    <span
                      className={`shrink-0 rounded-md px-2 py-0.5 text-[11px] font-semibold ${
                        hint
                          ? 'bg-amber-100 text-amber-900'
                          : order.status === 'cancelled'
                            ? 'bg-black/[0.06] text-muted'
                            : bucket === 'done'
                              ? 'bg-emerald-50 text-emerald-800'
                              : 'bg-black/[0.06] text-muted'
                      }`}
                    >
                      {hint || (factoryStep && bucket === 'active' ? factoryStep : statusText)}
                    </span>
                  </div>
                  <div className="font-mono text-sm text-ink pr-16">订单码 {order.orderCode || '—'}</div>
                  <div className="text-xs text-muted pr-16">
                    {metaPrimary}
                    {' · '}
                    {when}
                    {Number(order.balanceDueFen || 0) > 0
                      ? ` · 需补页量 ¥${(Number(order.balanceDueFen) / 100).toFixed(2)}`
                      : ''}
                  </div>
                </button>

                {order.status === 'open' ? (
                  <div className="border-t border-border/70 px-3 py-2">
                    <button
                      type="button"
                      disabled={busyId === order.id}
                      onClick={() => void onCancel(order.id)}
                      className="rounded-lg border border-danger/40 bg-[#FFF3F3] px-3 py-1.5 text-sm font-medium text-danger transition hover:bg-[#FFE8EA] disabled:opacity-50"
                    >
                      {busyId === order.id ? '取消中…' : '取消订单'}
                    </button>
                  </div>
                ) : null}
                {batches.some((b) => b.processingAuthorization === 'awaiting_owner_auth') ? (
                  <div className="space-y-2 border-t border-border/70 px-3 py-2">
                    {batches.map((batch) => renderAuthBlock(batch))}
                  </div>
                ) : null}
                {releaseUi ? renderReleaseRow(order) : null}
              </li>
            );
          })}
        </ul>

        {showOrphans ? (
          <div className="mt-4 space-y-2 rounded-xl border border-amber-200 bg-amber-50/80 px-3 py-2">
            <p className="text-xs font-medium text-amber-900">其他待授权批次</p>
            {orphanPending.map((batch) => (
              <div
                key={batch.id}
                className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-100 py-2 last:border-0"
              >
                <div className="min-w-0 text-sm">
                  <div className="truncate font-medium text-ink">{batch.title || '未命名批次'}</div>
                  <div className="text-xs text-muted">待授权 · 版面加工未启动</div>
                </div>
                {renderAuthActions(batch.id)}
              </div>
            ))}
          </div>
        ) : null}
      </section>

      {typeof document !== 'undefined' && dialog ? createPortal(dialog, document.body) : null}
      {typeof document !== 'undefined' && acceptDialogNode
        ? createPortal(acceptDialogNode, document.body)
        : null}
    </div>
  );
}
